import { createSlice } from '@reduxjs/toolkit';
import { resetAdminPersistedJourneyState } from 'store/adminJourneyReset';

import {
  EMPTY_END_USER_DASHBOARD_STEPS,
  type EndUserDashboardSteps,
} from 'services/endUser/endUserDashboardSteps.data';

import type { Expense } from 'pages/confirmCashback/types';

export interface AuthState {
  orgId: string | null;
  euId: string | null;
  unitId: string | null;
  user: unknown | null;
  /** From GET /eu/{euId}/state; used for end-user dashboard greeting. */
  endUserFirstName: string | null;
  /** From GET /eu/{euId}/state; used on complete-profile HSA contribution question. */
  endUserOrgName: string | null;
  /** From GET /eu/{euId}/state; preferred label on complete-profile HSA contribution question. */
  endUserUnitName: string | null;
  /** From GET /eu/{euId}/state; drives end-user route guards and side menu. */
  endUserAccountState: string | null;
  /** From GET /eu/{euId}/state `state` field (e.g. profile_enhanced). */
  endUserProfileState: string | null;
  /** From GET /eu/{euId}/state; expense certification consent accepted. */
  endUserExpenseConfirmationConsentAccepted: boolean;
  /** From GET /eu/{euId}/state `steps`; payout setup progress flags. */
  endUserDashboardSteps: EndUserDashboardSteps;
  /** From GET /eu/{euId}/state; drives end-user route guards and side menu. */
  endUserHasExpenses: boolean;
  /** True after GET /eu/{euId}/state has settled for the current session credentials. */
  endUserDashboardStateResolved: boolean;
  /** First pending expense loaded from GET …/unit/{unitId}/expenses/pending (id = backend expense id). */
  pendingCashbackExpense: Expense | null;
}

const initialState: AuthState = {
  orgId: null,
  euId: null,
  unitId: null,
  user: null,
  endUserFirstName: null,
  endUserOrgName: null,
  endUserUnitName: null,
  endUserAccountState: null,
  endUserProfileState: null,
  endUserExpenseConfirmationConsentAccepted: false,
  endUserDashboardSteps: EMPTY_END_USER_DASHBOARD_STEPS,
  endUserHasExpenses: false,
  endUserDashboardStateResolved: false,
  pendingCashbackExpense: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setOrgId(state, action: { payload: string | null }) {
      state.orgId = action.payload;
    },
    setEuId(state, action: { payload: string | null }) {
      state.euId = action.payload;
    },
    setUnitId(state, action: { payload: string | null }) {
      state.unitId = action.payload;
    },
    setUser(state, action: { payload: unknown | null }) {
      state.user = action.payload;
    },
    setEndUserFirstName(state, action: { payload: string | null }) {
      state.endUserFirstName = action.payload;
    },
    setEndUserOrgName(state, action: { payload: string | null }) {
      state.endUserOrgName = action.payload;
    },
    setEndUserUnitName(state, action: { payload: string | null }) {
      state.endUserUnitName = action.payload;
    },
    setEndUserAccountState(state, action: { payload: string | null }) {
      state.endUserAccountState = action.payload;
    },
    setEndUserProfileState(state, action: { payload: string | null }) {
      state.endUserProfileState = action.payload;
    },
    setEndUserExpenseConfirmationConsentAccepted(state, action: { payload: boolean }) {
      state.endUserExpenseConfirmationConsentAccepted = action.payload;
    },
    setEndUserDashboardSteps(state, action: { payload: EndUserDashboardSteps }) {
      state.endUserDashboardSteps = action.payload;
    },
    setEndUserHasExpenses(state, action: { payload: boolean }) {
      state.endUserHasExpenses = action.payload;
    },
    setEndUserDashboardStateResolved(state, action: { payload: boolean }) {
      state.endUserDashboardStateResolved = action.payload;
    },
    setPendingCashbackExpense(state, action: { payload: Expense | null }) {
      state.pendingCashbackExpense = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(resetAdminPersistedJourneyState, () => ({ ...initialState }));
  },
});

export const {
  setOrgId,
  setEuId,
  setUnitId,
  setUser,
  setEndUserFirstName,
  setEndUserOrgName,
  setEndUserUnitName,
  setEndUserAccountState,
  setEndUserProfileState,
  setEndUserExpenseConfirmationConsentAccepted,
  setEndUserDashboardSteps,
  setEndUserHasExpenses,
  setEndUserDashboardStateResolved,
  setPendingCashbackExpense,
} = authSlice.actions;
export default authSlice.reducer;
